import { useCallback, useMemo } from 'react'
import { useViewActions, useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { viewChrome } from '../../view-registry'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { SidepanelHeaderNavButton } from './sidepanel-header-nav-button'

interface SidepanelHeaderBackButtonProps {
  /**
   * Home navigation's only control, with no forward button beside it: framed,
   * showing the view a click lands on, and a tooltip in place of the trail.
   */
  standalone?: boolean
}

export function SidepanelHeaderBackButton({ standalone }: SidepanelHeaderBackButtonProps) {
  const { closeView } = useViewActions()
  const { topView, parentView, backTrail, canGoBack } = useViewNavigation()

  const tooltipText = useMemo(() => {
    if (topView.sourceLabel) {
      return `Back to ${topView.sourceLabel}`
    }

    if (parentView) {
      const parentLabel = viewChrome(parentView)?.label
      if (parentLabel) return `Back to ${parentLabel}`
    }

    return 'Back to Home'
  }, [topView, parentView])

  const handleBack = useCallback(() => {
    closeView()
    AmplitudeRenderer.trackEvent('Sidepanel Header', {
      action: 'close drawer',
      fromView: topView.id,
      toView: parentView?.id,
    })
  }, [closeView, topView, parentView])

  // Beside the forward button, both chevrons stay mounted whenever either
  // direction is live, so the pair reads as one control; the dead direction
  // greys out instead of vanishing.
  return (
    <SidepanelHeaderNavButton
      direction="back"
      // A single-entry trail never opens a menu, so only the tooltip shows.
      trail={standalone ? backTrail.slice(0, 1) : backTrail}
      disabled={!canGoBack}
      onNavigate={handleBack}
      tooltip={tooltipText}
      showDestination={standalone}
    />
  )
}
